import { DefaultOpenModalProps } from 'components/button/OpenModalButton/OpenModalButton';
import styles from './TermsOfServiceModal.module.scss';
import { useAuth } from 'hooks';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import { List, ListItem } from '@mui/material';
import { FormModal } from 'components/form/FormModal/FormModal';
import * as yup from 'yup';
import { FormCheckbox, FormSubmitButton } from 'components/form';
import { FieldValues, useFormContext } from 'react-hook-form';
import { updateUserTermsOfService } from 'lib/firebase';
import { activateSnackbar } from 'store/features/ui';
import { useAppDispatch } from 'store';

interface TermsOfService {
    accepted: boolean;
}

interface TosFormButtonProps {
    onSubmit: (data: TermsOfService) => void;
    onError: (data: FieldValues) => void;
}

export const TosFormButton = ({ onSubmit, onError }: TosFormButtonProps) => {
    // This `watch` has to be wrapped within a Form component which is why this part is a separate component from the rest of the TOS modal.
    const { watch } = useFormContext();
    const hasAccepted = watch('accepted');

    return (
        <FormSubmitButton
            onErrorHandler={onError}
            onValidHandler={onSubmit}
            buttonLabel="Continue"
            disabled={!hasAccepted}
            fullWidth={false}
        />
    );
};

export const TermsOfServiceModal = ({
    modalOpen,
    setModalOpen,
}: DefaultOpenModalProps) => {
    const tosDefaultValues: TermsOfService = { accepted: false };
    const tosSchema = yup.object({ accepted: yup.boolean().required() });
    const { user } = useAuth();
    const dispatch = useAppDispatch();

    const onSubmit = (data: TermsOfService) => {
        if (data.accepted && user?.uid) {
            updateUserTermsOfService(user.uid, data.accepted).then(() => {
                setModalOpen(false);
            });
        }
    };

    const onError = (error: FieldValues) => {
        const messages = Object.keys(error).map((key) => error[key]?.message);
        dispatch(
            activateSnackbar({
                message: JSON.stringify(messages),
                severity: 'error',
            })
        );
    };

    return (
        <FormModal
            testId="TermsOfServiceModal"
            modalOpen={modalOpen}
            onClose={() => setModalOpen(false)}
            title={'Terms of Service'}
            fullWidth
            defaultValues={tosDefaultValues}
            validationSchema={tosSchema}
            setModalOpen={setModalOpen}
            hideDefaultSubmit
            disallowUserClose
            actionChildren={
                <div className={styles.actions}>
                    <FormCheckbox
                        field="accepted"
                        label="I accept the Terms of Service"
                    ></FormCheckbox>
                    <TosFormButton onSubmit={onSubmit} onError={onError} />
                </div>
            }
        >
            <DialogContent className={styles.TermsOfServiceModal}>
                <Typography variant="h2">
                    Tank Utility Terms of Service
                </Typography>

                <Typography sx={{ marginBottom: '2em' }}>
                    Tank Utility, Inc. and its subsidiaries and affiliates
                    (collectively, “Tank Utility”) provides: (1) a Tank Utility
                    user account website that may be accessed at tankutility.com
                    or portal.tankutility.com or apps.tankutility.com or
                    tankutility.firebaseapp.com (“Sites”), (2) services
                    accessible through the Sites (“Web Apps”), and (3) software
                    that may be downloaded to your smartphone or tablet to
                    access services (“Mobile Apps”), all for use in conjunction
                    with Tank Utility hardware products (“Products”) and in
                    other ways that Tank Utility provides. Some Products and
                    Services can be used together or in ways that integrate with
                    products and services from third parties. The term
                    “Services” means the Sites, Web Apps, and Mobile Apps.
                </Typography>

                <Typography sx={{ marginBottom: '2em' }}>
                    These Terms of Service (“Terms”) govern your access to, and
                    use of the Services. These Terms give you specific legal
                    rights. In addition, you may also have other legal rights,
                    which vary from jurisdiction to jurisdiction. The
                    disclaimers, exclusions and limitations of liability under
                    these Terms will not apply to the extent prohibited by
                    applicable law. Some jurisdictions do not allow the
                    exclusion of implied warranties, or the exclusion or
                    limitation of incidental or consequential damages or other
                    rights, so those provisions of these Terms may not apply to
                    you. THIS IS A LEGAL AGREEMENT. BY ACCESSING AND USING THE
                    SERVICES (INCLUDING THE SITES), YOU ARE ACCEPTING AND
                    AGREEING TO THESE TERMS ON BEHALF OF YOURSELF OR THE ENTITY
                    THAT YOU REPRESENT IN CONNECTION WITH SUCH ACCESS AND USE.
                    YOU REPRESENT AND WARRANT THAT YOU HAVE THE RIGHT, AUTHORITY
                    AND CAPACITY TO ACCEPT AND AGREE TO THESE TERMS ON BEHALF OF
                    YOURSELF OR THE ENTITY THAT YOU REPRESENT. YOU REPRESENT
                    THAT YOU ARE OF SUFFICIENT LEGAL AGE IN YOUR JURISDICTION OF
                    RESIDENCE TO USE OR ACCESS THE SERVICES AND TO ENTER INTO
                    THIS AGREEMENT. IF YOU DO NOT AGREE WITH ANY OF THE
                    PROVISIONS OF THESE TERMS, YOU SHOULD DISCONNECT YOUR
                    PRODUCTS FROM YOUR ACCOUNT (AS DESCRIBED BELOW) AND CEASE
                    ACCESSING OR USING THE SERVICES. AS DESCRIBED BELOW, YOU ARE
                    CONSENTING TO AUTOMATIC SOFTWARE UPDATE OF THE SERVICES AND
                    OF THE PRODUCTS CONNECTED TO THE SERVICES. IF YOU DO NOT
                    AGREE, YOU SHOULD NOT USE THE SERVICES. AS DESCRIBED BELOW,
                    SECTIONS 4 AND 5 DESCRIBE IMPORTANT LIMITATIONS OF THE
                    SERVICES, ESPECIALLY IN CONNECTION WITH LIFE SAFETY AND
                    CRITICAL USES. PLEASE READ THESE DISCLOSURES CAREFULLY, AS
                    YOU ARE ACKNOWLEDGING THEM AND ACCEPTING THEM.
                </Typography>

                <List>
                    <ListItem sx={{ fontWeight: 'bold' }}>
                        1. Overview, Eligibility, Customer Service, Term and
                        Termination
                    </ListItem>
                </List>

                <List>
                    <ListItem>
                        (a) Overview and Relation to Other Agreements. These
                        Terms govern your use of the Services. Your purchase of
                        any Product is governed by the limited warranty provided
                        with that Product (“Limited Warranty”) and may further
                        be governed by the Terms & Conditions of Sale. The
                        software embedded in the Product (and any updates
                        thereto) (“Product Software”) is licensed and governed
                        by the documents provided with the Products and Product
                        Software (as may be updated by Tank Utility from time to
                        time). Certain features of the Services may be subject
                        to additional guidelines, terms or rules, information
                        security policies, and/or privacy policies which will be
                        posted on the Services in connection with such features.
                        All additional guidelines, terms or rules, and policies
                        are incorporated by reference into these Terms, and you
                        are agreeing to accept and abide by them by using the
                        Services.
                    </ListItem>
                    <ListItem>
                        (b) Eligibility. You may use the Services only if you
                        can form a binding contract with Tank Utility, and only
                        if you are in compliance with these Terms and all
                        applicable local, state/provincial, national and
                        international laws, rules and regulations. Any use or
                        access to the Services by anyone under the age of 18 is
                        strictly prohibited and is a violation of these Terms.
                        The Services are not available to any users previously
                        prohibited from using the Services by Tank Utility.
                    </ListItem>
                    <ListItem>
                        (c) Customer Service. If you have any questions or
                        concerns regarding the Products, the Services or these
                        Terms, please contact Tank Utility as described in these
                        Terms.
                    </ListItem>
                    <ListItem>
                        (d) Term and Termination. These Terms will remain in
                        full force and effect so long as you continue to access
                        or use the Services, or until terminated in accordance
                        with the provisions of these Terms. At any time, Tank
                        Utility may (i) suspend or terminate your rights to
                        access or use the Services, or (ii) terminate these
                        Terms with respect to you if Tank Utility, in good
                        faith, believes that you have used the Services in
                        violation of these Terms, including any incorporated
                        guidelines, terms or rules. If you transfer a Product to
                        a new owner, your right to use the Services with respect
                        to that Product automatically terminates, and the new
                        owner will have no right to use the Product or Services
                        under your Account (as described below) and will need to
                        register for a separate Account with Tank Utility.
                    </ListItem>
                    <ListItem>
                        (e) Effect of Termination. Upon termination of these
                        Terms, your Account and your right to use the Services
                        will automatically terminate.
                    </ListItem>
                </List>

                <List>
                    <ListItem sx={{ fontWeight: 'bold' }}>2. Accounts</ListItem>
                </List>
                <Typography sx={{ marginBottom: '2em' }}>
                    To use the Services, you must register for a user account
                    (“Account”) and provide certain information about yourself,
                    as prompted by the applicable registration form. You
                    represent and warrant that: (a) all required registration
                    information that you submit is truthful and accurate; (b)
                    you will maintain the accuracy of such information; and (c)
                    your use of the Services does not violate any US or other
                    applicable law or regulation (e.g. you are not located in an
                    embargoed country or are not listed as a prohibited or
                    restricted party under applicable export control laws and
                    regulations). You are entirely responsible for maintaining
                    the confidentiality of your Account login information and
                    for all activities that occur under your Account. You agree
                    to use “strong” passwords (passwords that use a combination
                    of upper- and lower-case letters, numbers and symbols) with
                    your Account and to maintain your password securely to
                    prevent others from gaining access without your permission.
                    You agree not to share your Account or the password for your
                    Account with anyone else. You agree to notify Tank Utility
                    immediately of any unauthorized use or suspected
                    unauthorized use of your Account, or any other breach of
                    security. Tank Utility is not liable for any loss or damage
                    arising from your failure to comply with the requirements
                    above.
                </Typography>

                <List>
                    <ListItem sx={{ fontWeight: 'bold' }}>
                        3. Access to Services
                    </ListItem>
                </List>
                <List>
                    <ListItem>
                        (a) Access and Use. Subject to these Terms, Tank Utility
                        grants you a non-transferable, non-exclusive right
                        (without the right to sub-license) to access and use the
                        Services by (i) using the Web Apps in connection with,
                        and solely for the purpose of controlling and monitoring
                        the Products installed on your property or otherwise
                        accessing a service explicitly provided by Tank Utility
                        for your use (the “Permitted Purpose”), and (ii)
                        installing and using the Mobile Apps solely on your own
                        handheld mobile device (e.g. iPhone, iPad or Android
                        smartphone) and solely for the Permitted Purpose, and
                        (iii) if separately authorized, accessing a third-party
                        service though an authorized account and the interface
                        provided by Tank Utility.
                    </ListItem>
                    <ListItem>
                        (b) Automatic Software Updates. Tank Utility may, from
                        time to time, develop patches, bug fixes, updates,
                        upgrades and other modifications to improve the
                        performance of the Services and/or the Product Software
                        (“Updates”). These may be automatically installed
                        without providing any additional notice or receiving any
                        additional consent. You consent to this automatic
                        update. If you do not want such Updates, your remedy is
                        to terminate your Account and stop using the Services
                        and the Product. If you do not terminate a previously
                        created Account, you will receive Updates automatically.
                        You acknowledge that you may be required to install
                        Updates to use the Services and the Product, and you
                        agree to install any Updates that Tank Utility provides
                        promptly. Your continued use of the Services and the
                        Product is your agreement – (i) to these Terms with
                        respect to the Services, and (ii) to the documents
                        provided with the Products and Product Software (as may
                        be updated by Tank Utility from time to time) with
                        respect to updated Product Software.
                    </ListItem>
                    <ListItem>
                        (c) Interface to Third-Party Products and Services. Over
                        time, Tank Utility may provide the opportunity for you
                        to interface the Products and Services to one or more
                        third-party products and services, through and using the
                        Services, for example through the Works with Tank
                        Utility platform (“Third-Party Products and Services”).
                        You decide whether, and with which Third-Party Products
                        and Services, you want to interface. Your explicit
                        consent and authorization is required for this interface
                        and is revocable by you at any time. Once your consent
                        is given for a particular Third-Party Product and
                        Service, you agree that Tank Utility may exchange
                        information and control data regarding you, your usage
                        of the Services and Product, and your products,
                        including your personal information and your User
                        Submissions, in order to enable the interface that you
                        have authorized. Once this information is shared with
                        the Third-Party Products and Services, its use will be
                        governed by the third party’s privacy policy and not by
                        Tank Utility’s privacy documentation and Tank Utility
                        has no responsibility or liability for any disclosure of
                        any of your information by or through any Third-Party
                        Products and Services. Further, you acknowledge and
                        agree that Tank Utility has no obligation to exchange or
                        share any of your information or control data with any
                        third party and that you remain responsible for ensuring
                        that the third parties obtain the information you want
                        them to obtain. You acknowledge and agree that Tank
                        Utility makes no representation or warranty about the
                        safety of any Third-Party Products and Services.
                        Accordingly, Tank Utility is not responsible for your
                        use of any Third-Party Products and Services, or any
                        personal injury, death, property damage (including,
                        without limitation, to your home), or other harm or
                        losses arising from or relating to your use of any
                        Third-Party Products and Services. You should contact
                        the third party with any questions about their
                        Third-Party Products and Services.
                    </ListItem>
                    <ListItem>
                        (d) Certain materials may be displayed or performed on
                        the Services (including, but not limited to text,
                        graphics, articles, photographs, video, images and
                        illustrations (“Content”). The Content also includes
                        information that you and other users provide us with in
                        the course of using the Services (collectively, “User
                        Submissions”), which we may use to provide, maintain and
                        improve the Services. Some Content may be visible to
                        others (for example, the Service allows you to upload,
                        post or otherwise share video content). You may also
                        post feedback, comments, questions or other information
                        on the Sites. You are solely responsible for all Content
                        that you upload, post, email, transmit or otherwise
                        disseminate using or in connection with the Services, or
                        that you contribute in any manner to the Services; you
                        represent and warrant that you have all rights necessary
                        to do so in the manner in which you contribute it; and
                        you license to Tank Utility all patent, trademark, trade
                        secret, copyright and other proprietary rights in and to
                        such Content for publication on and other usage related
                        to the Services pursuant to these Terms. You shall abide
                        by all copyright notices, trademark rules, information
                        and restrictions contained in any Content accessed
                        through the Services, and shall not use, copy,
                        reproduce, modify, translate, publish, broadcast,
                        transmit, distribute, perform, upload, display, license,
                        sell or otherwise exploit for any purposes whatsoever
                        any Content or third-party submissions or other
                        proprietary rights not owned by you: (i) without the
                        express prior written consent of the respective owners,
                        and (ii) in any way that violates any third-party right.
                        Tank Utility reserves the right to remove any Content
                        from the Services at any time, for any reason
                        (including, but not limited to upon receipt of claims or
                        allegations from third parties or authorities relating
                        to such Content or if we are concerned that you may have
                        breached the immediately preceding sentence), or for no
                        reason at all.
                    </ListItem>
                    <ListItem>
                        (e) Certain Restrictions. The rights granted to you in
                        these Terms are subject to the following restrictions:
                        (i) you agree not to license, sell, rent, lease,
                        transfer, assign, distribute, host or otherwise
                        commercially exploit the Services; (ii) you agree not to
                        modify, make derivative works of, disassemble, reverse
                        compile or reverse engineer any part of the Services;
                        (iii) you agree not to access the Services in order to
                        build a similar or competitive service; (iv) except as
                        expressly stated herein, no part of the Services may be
                        copied, reproduced, distributed, republished,
                        downloaded, displayed, posted or transmitted in any form
                        or by any means; (v) you agree not to upload, transmit
                        or distribute any computer viruses, worms or any
                        software intended to damage or alter a computer or
                        communications network, computer, handheld mobile
                        device, data, the Services, the Product, the Product
                        Software or any other system, device or property; (vi)
                        you agree not to interfere with, disrupt or attempt to
                        gain unauthorized access to the servers or networks
                        connected to the Services, or violate the regulations,
                        policies or procedures of such networks; (vii) you agree
                        not to access (or attempt to access) any of the Services
                        by means other than through the interface that is
                        provided by Tank Utility; and (viii) you agree not to
                        remove, obscure or alter any proprietary rights notices
                        (including copyrights and trademark notices) that may be
                        contained in, or displayed in connection with the
                        Services. Any future release, update or other addition
                        to functionality of the Services shall be subject to
                        these Terms.
                    </ListItem>
                    <ListItem>
                        (f) Open Source. Certain items of independent,
                        third-party code may be included in the Web Apps and/or
                        Mobile Apps that are subject to the GNU General Public
                        License (“GPL”) or other open-source licenses
                        (“Open-Source Software”). The Open-Source Software is
                        licensed under the terms of the license that accompanies
                        such Open-Source Software. Nothing in these Terms limits
                        your rights under, or grants you rights that supersede
                        the terms and conditions of any applicable end-user
                        license for such Open-Source Software. In particular,
                        nothing in these Terms restricts your right to copy,
                        modify and distribute such Open- Source Software that is
                        subject to the terms of the GPL.
                    </ListItem>
                    <ListItem>
                        (g) Privacy Statements. Please review all applicable
                        privacy statements that Tank Utility may provide from
                        time to time, which may, among other things, describe
                        practices regarding the information that Tank Utility
                        may collect from users of the Products and Services,
                        including any Content or User Submissions.
                    </ListItem>
                    <ListItem>
                        (h) Security. Tank Utility cares about the integrity and
                        security of your personal information. However, Tank
                        Utility cannot guarantee that unauthorized third parties
                        will never be able to defeat our security measures or
                        use your personal information for improper purposes. You
                        acknowledge that you provide your personal information
                        at your own risk.
                    </ListItem>
                    <ListItem>
                        (i) Modification. Tank Utility reserves the right, at
                        any time, to modify, suspend or discontinue the Services
                        or any part thereof with or without notice. You agree
                        that Tank Utility will not be liable to you or to any
                        third party for any modification, suspension or
                        discontinuance of the Services or any part thereof.
                    </ListItem>
                    <ListItem>
                        (j) Access Outside Certain Countries. Although the Sites
                        are accessible worldwide, the Products and Services
                        provided or accessed through or on the Sites are not
                        available to all persons or in all countries. If you
                        choose to access the Sites from outside a country in
                        which Tank Utility supports the Product and Services
                        described in these Terms (“Permitted Country”), you do
                        so on your own initiative and you are solely responsible
                        for complying with applicable local laws in your
                        country. You understand and accept that the Sites are
                        not designed for use in a non-Permitted Country and that
                        some, or all of the features of the Sites may not work
                        or be appropriate for use in such a country. To the
                        extent permissible by law, Tank Utility accepts no
                        responsibility or liability for any damage or loss
                        caused by your access or use of the Sites or Products in
                        a non-Permitted Country. You will be bound by these
                        Terms wherever you access or use the Sites or use the
                        Services.
                    </ListItem>
                </List>

                <List>
                    <ListItem sx={{ fontWeight: 'bold' }}>
                        4. Agreed Usage and Limitations of Tank Utility Services
                    </ListItem>
                </List>
                <List>
                    <ListItem>
                        (a) Intended Use of Tank Utility Services. The Services
                        are intended to be accessed and used for
                        non-time-critical information and control of Products.
                        While we aim for the Services to be highly reliable and
                        available, they are not intended to be reliable or
                        available 100% of the time. The Services are subject to
                        sporadic interruptions and failures for a variety of
                        reasons beyond Tank Utility’s control, including Wi-Fi
                        intermittency, service provider downtime, mobile
                        notifications and operators, and the other reasons
                        described in these Terms, among others. You acknowledge
                        these limitations and agree that Tank Utility is not
                        responsible for any damages allegedly caused by the
                        failure or delay of the Services to reflect current
                        status or notifications.
                    </ListItem>
                    <ListItem>
                        (b) No Life Safety or Critical Uses of the Services. You
                        acknowledge and agree that the Products and Services,
                        whether standing alone or when interfaced with
                        third-party products or services, are not certified or
                        intended for emergency response. Tank Utility makes no
                        warranty or representation that use of the Products or
                        Services with any third-party product or service will
                        affect or increase any level of safety. YOU UNDERSTAND
                        THAT THE PRODUCTS AND SERVICES, WHETHER STANDING ALONE
                        OR INTERFACED WITH THIRD-PARTY PRODUCTS AND SERVICES,
                        ARE NOT A THIRD-PARTY MONITORED EMERGENCY NOTIFICATION
                        SYSTEM – TANK UTILITY WILL NOT DISPATCH EMERGENCY
                        AUTHORITIES TO YOUR HOME IN THE EVENT OF AN EMERGENCY.
                        In addition, the Tank Utility Customer Care and Support
                        contacts cannot be considered a lifesaving solution for
                        people at risk in the home, and they are no substitute
                        for emergency services. All life-threatening and
                        emergency events should be directed to the appropriate
                        response services.
                    </ListItem>
                    <ListItem>
                        (c) Reliability of Notifications. You acknowledge that
                        the Services, including remote access and mobile
                        notifications, are not intended to be 100% reliable or
                        100% available. We cannot, and do not guarantee that you
                        will receive notifications in any given time, or at all.
                        YOU AGREE THAT YOU WILL NOT RELY ON THE SERVICES OR
                        PRODUCTS FOR ANY LIFE SAFETY OR CRITICAL PURPOSES.
                        MOBILE NOTIFICATIONS REGARDING THE STATUS AND ALARMS ON
                        YOUR PRODUCTS ARE PROVIDED FOR INFORMATIONAL PURPOSES
                        ONLY – THEY ARE NOT A SUBSTITUTE FOR A THIRD-PARTY,
                        MONITORED EMERGENCY NOTIFICATION SYSTEM. You acknowledge
                        that it is your responsibility to educate yourself on
                        how to respond to an emergency and to respond according
                        to the specifics of your situation.
                    </ListItem>
                    <ListItem>
                        (d) Temporary Suspension. The Services may be suspended
                        temporarily, without notice, for security reasons,
                        systems failure, maintenance and repair, or other
                        circumstances. You agree that you will not be entitled
                        to any refund or rebate for such suspensions. Tank
                        Utility does not offer any specific uptime or
                        availability guarantee for the Services.
                    </ListItem>
                    <ListItem>
                        (e) Systems Requirements. The Services will not be
                        accessible without: (i) a working Wi-Fi network in your
                        home or third party cellular service that is positioned
                        to communicate reliably with the Products; (ii) an
                        Account; (iii) mobile clients, such as a supported phone
                        or tablet (required for some functionality); (iv)
                        always-on broadband Internet access in your home; (v)
                        proper installation and continued operation of the
                        Products; and (vi) other systems elements that may be
                        specified by Tank Utility. It is your responsibility to
                        ensure that you have all required system elements and
                        that they are compatible and properly configured. You
                        acknowledge that the Services may not work as described
                        when the requirements and compatibility have not been
                        met.
                    </ListItem>
                    <ListItem>
                        (f) Efficiency, Energy Savings, and other Benefits. Tank
                        Utility does not guarantee or promise any specific
                        monetary benefit from the use of the Products or
                        Services or any feature of them. Actual monetary
                        benefits vary with factors beyond Tank Utility’s control
                        or knowledge. From time to time, Tank Utility may use
                        the Services to provide you with information that is
                        unique to you and your energy usage, and may suggest an
                        opportunity to save money on energy bills or delivery
                        scheduling if you adopt suggestions or features of the
                        Product or Services. We do this to highlight an
                        opportunity based on our analysis and information about
                        you and your household. You acknowledge that these
                        promotions are not a guarantee of actual savings, and
                        you agree not to seek monetary or other remedies from
                        Tank Utility if your savings differ.
                    </ListItem>
                    <ListItem>
                        (g) The Services may provide you with information
                        (“Product Information”) regarding the Products in your
                        home and their connection with other products and
                        services. All Product Information is provided “as is”
                        and “as available”. We cannot guarantee that it is
                        correct or up to date. In cases where it is critical,
                        accessing Product Information through the Services is
                        not a substitute for direct access of the information in
                        the home.
                    </ListItem>
                    <ListItem>
                        (h) All information publicly posted or privately
                        transmitted through the Services is the sole
                        responsibility of the person from which such Content
                        originated, and Tank Utility will not be liable for any
                        errors or omissions in any Content. Tank Utility cannot
                        guarantee the identity of any other users with whom you
                        may interact in the course of using the Services. In
                        addition, we cannot guarantee the authenticity of any
                        data that users or merchants may provide about
                        themselves. You acknowledge that all Content accessed by
                        you using the Services is at your own risk, and you will
                        be solely responsible for any damage or loss to any
                        party resulting therefrom. We cannot control, and have
                        no duty to take any action regarding how you may
                        interpret and use the Content or what actions you may
                        take as a result of having been exposed to the Content,
                        and you hereby release us from all liability for you
                        having acquired or not acquired Content through the
                        Services.
                    </ListItem>
                    <ListItem>
                        (i) You warrant, represent and agree that you will not
                        contribute any Content or otherwise use the Services in
                        a manner that (i) infringes or violates the intellectual
                        property rights or proprietary rights, rights of
                        publicity or privacy, or other rights of any third
                        party; (ii) violates any law, statute, ordinance or
                        regulation; (iii) is harmful, fraudulent, deceptive,
                        threatening, abusive, harassing, tortious, defamatory,
                        vulgar, obscene, libelous or otherwise objectionable;
                        (iv) impersonates any person or entity, including,
                        without limitation, any employee or representative of
                        Tank Utility; (v) contains a virus, trojan horse, worm,
                        time bomb or other harmful computer code, file or
                        program; (vi) jeopardizes the security of your Account
                        or anyone else’s Account (such as allowing someone else
                        to log in to the Services as you); (vii) attempts, in
                        any manner, to obtain the password, account or other
                        security information from any other user; (viii)
                        violates the security of any computer network, or cracks
                        any passwords or security encryption codes; (ix) runs
                        Maillist, Listserv or any form of auto-responder or
                        “spam” on the Services, or any processes that otherwise
                        interfere with the proper working of the Services
                        (including by placing an unreasonable load on the
                        Services’ infrastructure); (x) copies or stores any
                        significant portion of the Content; or (xi) decompiles,
                        reverse engineers or otherwise attempts to obtain the
                        source code or underlying ideas or information of, or
                        relating to the Services.
                    </ListItem>
                </List>

                <List>
                    <ListItem sx={{ fontWeight: 'bold' }}>
                        5. Limitations of Tank Utility Services Due to Third
                        Parties.
                    </ListItem>
                </List>
                <List>
                    <ListItem>
                        (a) General. Tank Utility Services rely on, or
                        inter-operate with, third-party products and services.
                        These third-party products and services are beyond Tank
                        Utility’s control, but their operation may impact or be
                        impacted by the use and reliability of the Services. You
                        acknowledge and agree that: (i) the use and availability
                        of the Services is dependent on third-party product
                        vendors and service providers, (ii) these third-party
                        products and services may not operate in a reliable
                        manner 100% of the time and they may impact the way that
                        the Services operate or are available, and (iii) Tank
                        Utility is not responsible for damages and losses due to
                        the operation of these third-party products and
                        services.
                    </ListItem>
                    <ListItem>
                        (b) Third-Party Service Providers Used By Tank Utility.
                        You acknowledge that Tank Utility uses third-party
                        service providers to enable some aspects of the Services
                        – such as, for example, data storage, synchronization
                        and communication through Amazon Web Services, and
                        mobile device notifications through mobile operating
                        system vendors and mobile operators. YOU AGREE NOT TO
                        RELY ON THE SERVICES FOR ANY LIFE SAFETY OR
                        TIME-CRITICAL PURPOSES.
                    </ListItem>
                    <ListItem>
                        (c) Equipment, ISP and Operator. You acknowledge that
                        the availability of the Services is dependent on (i)
                        your computer, mobile device, home wiring, home Wi-Fi
                        network and other related equipment (“Equipment”), (ii)
                        your Internet service provider (“ISP”), and (iii) your
                        mobile device operator (“Operator”). You acknowledge
                        that you are responsible for all fees charged by your
                        ISP and Operator in connection with your use of the
                        Services. You also acknowledge that you are responsible
                        for compliance with all applicable agreements, terms of
                        use/service and other policies of your ISP and Operator.
                    </ListItem>
                    <ListItem>
                        (d) Third-Party Products and Services that Work With
                        Tank Utility Services. Tank Utility may provide the
                        opportunity for you to interface with Third-Party
                        Products and Services, for example through the Works
                        with Tank Utility platform. Although the Works with Tank
                        Utility platform is offered by Tank Utility, you
                        acknowledge that Third-Party Products and Services that
                        you connect to your account or interface with are not
                        Tank Utility products or services, and you acknowledge
                        and agree that Tank Utility does not control, and that
                        these Terms do not apply to, any Third-Party Products
                        and Services. Use of any Third-Party Products and
                        Services is governed by separate terms and conditions
                        provided by the operator(s) of the applicable
                        Third-Party Products and Services. You acknowledge and
                        agree that Tank Utility makes no representation or
                        warranty about the safety of any Third-Party Products
                        and Services. Accordingly, Tank Utility is not
                        responsible for your use of any Third-Party Products and
                        Services, or any personal injury, death, property damage
                        (including, without limitation, to your home), or other
                        harm or losses arising from, or relating to your use of
                        any Third-Party Products and Services. You should
                        contact the Third Party with any questions about their
                        Products and Services.
                    </ListItem>
                    <ListItem>
                        (e) App Stores. You acknowledge and agree that the
                        availability of the Mobile Apps is dependent on the
                        third-party websites from which you download the Mobile
                        Apps, e.g. the App Store from Apple or the Android app
                        market from Google (each an “App Store”). You
                        acknowledge that these Terms are between you and Tank
                        Utility and not with an App Store. Each App Store may
                        have its own terms and conditions to which you must
                        agree before downloading Mobile Apps from it. You agree
                        to comply with, and your license to use the Mobile Apps
                        is conditioned upon your compliance with such App Store
                        terms and conditions. To the extent that such other
                        terms and conditions from such App Store are less
                        restrictive than, or otherwise conflict with the terms
                        and conditions of these Terms, the more restrictive or
                        conflicting terms and conditions in these Terms apply.
                    </ListItem>
                    <ListItem>
                        (f) Third-Party Website Links and Referrals. The Sites
                        may contain links to other websites operated by third
                        parties (“Third-Party Sites”) and referrals to
                        third-party vendors (“Referred Vendors”). Such
                        Third-Party Sites and Referred Vendors are not under our
                        control. Tank Utility provides these links and referrals
                        only as a convenience and does not review, approve,
                        monitor, endorse, warrant or make any representations
                        with respect to such Third-Party Sites or Referred
                        Vendors. Your use of these Third-Party Sites and
                        Referred Vendors is at your own risk.
                    </ListItem>
                    <ListItem>
                        (g) Release Regarding Third Parties. Tank Utility is not
                        responsible for third parties or their products and
                        services, including, without limitation, the App Stores,
                        Third-Party Products and Services, Third-Party Sites,
                        Referred Vendors, Equipment, ISP and Operators. Tank
                        Utility hereby disclaims, and you hereby discharge,
                        waive and release Tank Utility and its licensors and
                        suppliers from any past, present and future claims,
                        liabilities and damages, known or unknown, arising out
                        of, or relating to your interactions with such third
                        parties and their products and services. YOU HEREBY
                        WAIVE CALIFORNIA CIVIL CODE SECTION 1542 IN CONNECTION
                        WITH THE FOREGOING, WHICH STATES: “A GENERAL RELEASE
                        DOES NOT EXTEND TO CLAIMS THAT THE CREDITOR DOES NOT
                        KNOW OR SUSPECT TO EXIST IN HIS OR HER FAVOUR AT THE
                        TIME OF EXECUTING THE RELEASE WHICH, IF KNOWN BY HIM OR
                        HER, MUST HAVE MATERIALLY AFFECTED HIS OR HER SETTLEMENT
                        WITH THE DEBTOR”. YOU HEREBY WAIVE ANY SIMILAR PROVISION
                        IN ANY OTHER JURISDICTION.
                    </ListItem>
                </List>

                <List>
                    <ListItem sx={{ fontWeight: 'bold' }}>
                        6. Ownership and Intellectual Property
                    </ListItem>
                </List>
                <List>
                    <ListItem>
                        (a) Tank Utility Property. You acknowledge that all
                        intellectual property rights, including, without
                        limitation, copyrights, patents, trademarks and trade
                        secrets, in the Product, Product Software and Services
                        (i.e. the Sites, Web Apps and Mobile Apps) are owned by
                        Tank Utility or its affiliates, or our licensors. Your
                        possession, access to and use of the Product, Product
                        Software and Services do not transfer to you or any
                        third party any rights, title or interest in, or to such
                        intellectual property rights. Tank Utility, and its
                        affiliates and licensors and suppliers, reserve all
                        rights not granted in these Terms. The Services are
                        licensed to you, not sold, under these Terms. You may
                        only copy parts of the Services (including these Terms)
                        onto your own computer for your own personal use. You
                        may not use the content of the Services in any other
                        public or commercial way, nor may you copy or
                        incorporate any of the content of the Services into any
                        other work, including your own website, without the
                        written consent of Tank Utility. You must have a license
                        from us before you can post or redistribute any portion
                        of the Services. Other than with respect to User
                        Submissions, Tank Utility retains full and complete
                        title to all content on the Services, including any
                        downloadable software and all data that accompanies it.
                        You must not copy, modify or in any way reproduce or
                        damage the structure or presentation of the Services, or
                        any content therein.
                    </ListItem>
                    <ListItem>
                        (b) Feedback. You may choose to, or Tank Utility may
                        invite you to submit comments, suggestions or ideas
                        about the Products or Services, including how to improve
                        the Products or Services (“Feedback”). By submitting any
                        Feedback, you agree that your submissions are voluntary,
                        gratuitous, unsolicited and without restriction, and
                        will not place Tank Utility under any fiduciary or other
                        obligation. Tank Utility may use, copy, modify, publish
                        or redistribute any and all Feedback and its contents
                        for any purpose and in any way without any compensation
                        to you. You also agree that Tank Utility does not waive
                        any rights to use similar or related ideas previously
                        known to Tank Utility, developed by its employees, or
                        obtained from other sources.
                    </ListItem>
                    <ListItem>
                        (c) User Submissions. You hereby grant us a
                        non-exclusive, worldwide, royalty-free, perpetual,
                        irrevocable, sub-licensable and transferable right to
                        access, display or otherwise use your User Submissions
                        (including all related intellectual property rights) in
                        connection with providing you with the Services, as may
                        otherwise be directed or authorized by you, and to
                        improve and expand the features and functionality of the
                        Services and Products for you and other Tank Utility
                        customers. You also hereby do, and shall grant to each
                        user of the Services a non-exclusive license to access
                        and use your User Submissions through the Services and
                        as permitted through the functionality of the Services
                        and under these Terms. Furthermore, you understand that
                        we retain the right to reformat, modify, create
                        derivative works of, excerpt and translate any User
                        Submissions submitted by you. For clarity, the foregoing
                        license grant to Tank Utility does not affect your
                        ownership of, or your right to grant additional licenses
                        to the material in your User Submissions, unless
                        otherwise agreed in writing.
                    </ListItem>
                </List>

                <List>
                    <ListItem sx={{ fontWeight: 'bold' }}>
                        7. Indemnity
                    </ListItem>
                </List>
                <Typography sx={{ marginBottom: '2em' }}>
                    You agree to defend, indemnify and hold Tank Utility and its
                    licensors and suppliers harmless from any damages,
                    liabilities, claims or demands (including costs and
                    attorneys’ fees) made by any third party due to, or arising
                    from (i) your use of the Products or Services, (ii) your
                    violation of these Terms, (iii) any User Submissions or
                    Feedback that you provide; or (iv) your violation of any law
                    or the rights of any third party. Tank Utility reserves the
                    right, at your expense, to assume the exclusive defense and
                    control of any matter for which you are required to
                    indemnify Tank Utility, and you agree to cooperate with our
                    defense of such claims. You agree not to settle any such
                    claim without Tank Utility’s prior written consent. Tank
                    Utility will use reasonable efforts to notify you of any
                    such claim, action or proceeding upon becoming aware of it.
                </Typography>

                <List>
                    <ListItem sx={{ fontWeight: 'bold' }}>
                        8. Warranty Disclaimers
                    </ListItem>
                </List>
                <List>
                    <ListItem>
                        (a) ANY WARRANTIES FOR THE PRODUCT AND PRODUCT SOFTWARE
                        ARE SET FORTH IN THE LIMITED WARRANTY AND THE EULA,
                        RESPECTIVELY.
                    </ListItem>
                    <ListItem>
                        (b) THE SERVICES (I.E. THE SITES, WEB APPS, MOBILE APPS)
                        ARE PROVIDED FOR YOUR CONVENIENCE, “AS IS” AND “AS
                        AVAILABLE”, AND TANK UTILITY, AND OUR LICENSORS AND
                        SUPPLIERS, EXPRESSLY DISCLAIM ANY AND ALL WARRANTIES AND
                        CONDITIONS OF ANY KIND, WHETHER EXPRESS OR IMPLIED,
                        INCLUDING THE WARRANTIES OR CONDITIONS OF
                        MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
                        ACCURACY AND NON-INFRINGEMENT.
                    </ListItem>
                    <ListItem>
                        (c) TANK UTILITY, AND OUR LICENSORS AND SUPPLIERS, MAKE
                        NO WARRANTY THAT DEFECTS WILL BE CORRECTED OR THAT THE
                        SERVICES: (I) WILL MEET YOUR REQUIREMENTS; (II) WILL BE
                        COMPATIBLE WITH YOUR HOME NETWORK, COMPUTER OR MOBILE
                        DEVICE; (III) WILL BE AVAILABLE ON AN UNINTERRUPTED,
                        TIMELY, SECURE OR ERROR-FREE BASIS; OR (IV) WILL BE
                        ACCURATE OR RELIABLE. NO ADVICE OR INFORMATION, WHETHER
                        ORAL OR WRITTEN, OBTAINED BY YOU FROM TANK UTILITY OR
                        THOUGH THE SERVICES, SHALL CREATE ANY WARRANTY.
                    </ListItem>
                    <ListItem>
                        (d) TANK UTILITY DOES NOT WARRANT, ENDORSE, GUARANTEE OR
                        ASSUME RESPONSIBILITY FOR ANY PRODUCT OR SERVICE
                        ADVERTISED OR OFFERED BY A THIRD PARTY THROUGH, OR IN
                        CONNECTION WITH THE PRODUCTS OR SERVICES (INCLUDING, BUT
                        NOT LIMITED TO THIRD-PARTY PRODUCTS AND SERVICES
                        CONNECTED THROUGH THE WORKS WITH TANK UTILITY PLATFORM),
                        OR ANY HYPERLINKED WEBSITE OR SERVICE, AND TANK UTILITY
                        WILL NOT BE A PARTY TO, OR IN ANY WAY MONITOR ANY
                        TRANSACTION BETWEEN YOU AND THIRD-PARTY PROVIDERS OF
                        SUCH PRODUCTS OR SERVICES.
                    </ListItem>
                    <ListItem>
                        (e) WHEN YOU INSTALL, SET UP OR USE PRODUCTS AND
                        SERVICES LIKE THOSE PROVIDED BY TANK UTILITY, YOU ARE
                        GIVEN THE OPPORTUNITY TO ALTER DEFAULTS OR CHOOSE
                        PARTICULAR SETTINGS. THE CHOICES YOU MAKE CAN CAUSE
                        DAMAGE OR LEAD TO NON-RECOMMENDED OPERATION OF YOUR
                        CONNECTED EQUIPMENT OR SYSTEMS. YOU ASSUME ALL LIABILITY
                        FOR SUCH DAMAGE WHEN YOU CHOOSE PARTICULAR SETTINGS, OR
                        SET OR ADJUST DEFAULTS.
                    </ListItem>
                    <ListItem>
                        (f) TANK UTILITY MAKES NO REPRESENTATIONS CONCERNING ANY
                        CONTENT CONTAINED IN, OR ACCESSED THROUGH THE SERVICES,
                        AND TANK UTILITY WILL NOT BE RESPONSIBLE OR LIABLE FOR
                        THE ACCURACY, COPYRIGHT COMPLIANCE, LEGALITY OR DECENCY
                        OF MATERIAL CONTAINED IN, OR ACCESSED THROUGH THE
                        SERVICES. TANK UTILITY MAKES NO REPRESENTATIONS OR
                        WARRANTIES REGARDING SUGGESTIONS OR RECOMMENDATIONS OF
                        SERVICES OR PRODUCTS OFFERED OR PURCHASED THROUGH THE
                        SERVICES.
                    </ListItem>
                </List>

                <List>
                    <ListItem sx={{ fontWeight: 'bold' }}>
                        9. Limitation of Liability
                    </ListItem>
                </List>
                <Typography sx={{ marginBottom: '2em' }}>
                    Nothing in these Terms, and in particular within this
                    “Limitation of Liability” clause, shall attempt to exclude
                    liability that cannot be excluded under applicable law.
                </Typography>

                <Typography sx={{ marginBottom: '2em' }}>
                    TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO
                    EVENT WILL (A) TANK UTILITY BE LIABLE FOR ANY INDIRECT,
                    CONSEQUENTIAL, EXEMPLARY, SPECIAL OR INCIDENTAL DAMAGES,
                    INCLUDING ANY DAMAGES FOR LOST DATA, LOST PROFITS, INJURY TO
                    PERSON, OR DAMAGE TO PROPERTY, ARISING FROM, OR RELATING TO
                    THE SERVICES OR THE PRODUCTS, EVEN IF TANK UTILITY KNEW, OR
                    SHOULD HAVE KNOWN OF THE POSSIBILITY OF SUCH DAMAGES, AND
                    (B) TANK UTILITY’S TOTAL CUMULATIVE LIABILITY ARISING FROM,
                    OR RELATED TO THE SERVICES AND THE PRODUCTS, WHETHER IN
                    CONTRACT OR TORT OR OTHERWISE, EXCEED THE FEES ACTUALLY PAID
                    BY YOU TO TANK UTILITY OR TANK UTILITY’S AUTHORISED RESELLER
                    FOR THE SERVICES OR THE PRODUCT AT ISSUE IN THE PRIOR 12
                    MONTHS (IF ANY). THIS LIMITATION IS CUMULATIVE AND WILL NOT
                    BE INCREASED BY THE EXISTENCE OF MORE THAN ONE INCIDENT OR
                    CLAIM. TANK UTILITY DISCLAIMS ALL LIABILITY OF ANY KIND OF
                    TANK UTILITY’S LICENSORS AND SUPPLIERS. UNDER NO
                    CIRCUMSTANCES WILL TANK UTILITY BE LIABLE IN ANY WAY FOR ANY
                    CONTENT, INCLUDING, BUT NOT LIMITED TO ANY ERRORS OR
                    OMISSIONS IN ANY CONTENT, OR ANY LOSS OR DAMAGE OF ANY KIND
                    INCURRED IN CONNECTION WITH USE OF, OR EXPOSURE TO ANY
                    CONTENT POSTED, EMAILED, ACCESSED, TRANSMITTED OR OTHERWISE
                    MADE AVAILABLE VIA THE SERVICES.
                </Typography>

                <List>
                    <ListItem sx={{ fontWeight: 'bold' }}>
                        10. Fees and Payment
                    </ListItem>
                </List>
                <Typography sx={{ marginBottom: '2em' }}>
                    Certain aspects of the Services may be provided for a fee.
                    You shall pay all applicable fees, as described on the
                    Sites, in connection with such Services selected by you.
                    Tank Utility reserves the right to change its price list and
                    to institute new charges at any time, upon notice to you,
                    which may be sent by email or posted on the Sites. Your use
                    of the Services following such notification constitutes your
                    acceptance of any new or increased charges. Any fees paid
                    hereunder for Services are non-refundable. If you have
                    purchased your Product from us, please refer to your Product
                    documentation for returns and warranty information, or
                    contact Tank Utility as described in these terms.
                </Typography>

                <List>
                    <ListItem sx={{ fontWeight: 'bold' }}>
                        11. Disputes and Arbitration
                    </ListItem>
                </List>
                <List>
                    <ListItem>
                        (a) Contact Tank Utility First. If a dispute arises
                        between you and Tank Utility, our goal is to learn about
                        and address your concerns. You agree that you will
                        notify Tank Utility about any dispute that you have with
                        Tank Utility regarding these Terms, or our Products or
                        Services, by contacting Tank Utility as described in
                        these Terms.
                    </ListItem>
                    <ListItem>
                        (b) Binding Arbitration. You and Tank Utility agree,
                        subject to clause 13(c) (Protection of Confidentiality
                        and Intellectual Property Rights), to submit any claim,
                        dispute, action, cause of action, issue or request for
                        relief arising out of, or relating to these Terms or
                        your use of the Services to binding arbitration rather
                        than by filing any lawsuit in any forum other than set
                        forth in this section. Further, you agree that
                        arbitration is final and binding, and subject to only
                        very limited review by a court. You also waive your
                        right to any form of appeal, review or recourse to any
                        court or other judicial authority, insofar as such
                        waiver may be validly made. This provision is intended
                        to be interpreted broadly to encompass all disputes or
                        claims arising out of, or relating to your use of the
                        Service. Subject to clause 13(c) (Protection of
                        Confidentiality and Intellectual Property Rights), any
                        dispute or claim made by you against us or us against
                        you arising out of, or relating to these Terms or your
                        use of the Services (whether based in contract, tort,
                        statute, fraud, misrepresentation or any other legal
                        theory) will be resolved by binding arbitration, except
                        that you may take claims to small-claims court if they
                        qualify for hearing by such a court.
                    </ListItem>
                    <ListItem>
                        (c) Arbitration Procedures. You must first present any
                        claim or dispute to us by contacting Tank Utility to
                        allow us an opportunity to resolve the dispute. You may
                        request arbitration if your claim or dispute cannot be
                        resolved within 60 days after presenting the claim or
                        dispute to Tank Utility. Tank Utility may request
                        arbitration against you at any time after it has
                        notified you of a claim or dispute, in accordance with
                        clause 13(f) (Notifications). The arbitration of any
                        dispute or claim shall be conducted in accordance with
                        the then-current and applicable rules of the American
                        Arbitration Association, as modified by this agreement.
                        The place of any arbitration will be Boston,
                        Massachusetts, USA, and will be conducted in the English
                        language. Claims will be heard by a single arbitrator.
                        The arbitrator may not award relief in excess of, or
                        contrary to what these Terms provide, order
                        consolidation or arbitration on a class-wide or
                        representative basis, award punitive or consequential
                        damages, or any other damages aside from the prevailing
                        party’s actual damages, or order injunctive or
                        declaratory relief, except that the arbitrator may award
                        on an individual basis damages required by statute and
                        may order injunctive or declaratory relief pursuant to
                        an applicable consumer protection statute. Any
                        arbitration shall be confidential, and neither you, nor
                        Tank Utility, nor the arbitrator may disclose the
                        existence, content or results of any arbitration except
                        as may be required by law or for purposes of enforcement
                        or appeal of the arbitration award. Judgment on any
                        arbitration award may be entered in any court having
                        proper jurisdiction. If any portion of this arbitration
                        clause is determined by a court to be inapplicable or
                        invalid, then the remainder shall still be given full
                        force and effect.
                    </ListItem>
                    <ListItem>
                        (d) No Class Actions. There shall be no right or
                        authority for any claims subject to this arbitration
                        clause to be arbitrated on a class-action or
                        consolidated basis, or on bases involving claims brought
                        in a purported representative capacity on behalf of the
                        general public (including, but not limited to as a
                        private attorney general). You hereby waive any right to
                        bring any action or claim (or to have any third party
                        bring any action or claim on your behalf) arising out of
                        or relating to the Services or the Product as a class
                        action or on a consolidated or other representative
                        basis.
                    </ListItem>
                    <ListItem>
                        (e) Fees and Expenses. All administrative fees and
                        expenses of arbitration will be divided equally between
                        you and Tank Utility. Each party will bear the expense
                        of its own counsel, experts, witnesses, and preparation
                        and presentation of evidence at the arbitration hearing.
                    </ListItem>
                    <ListItem>
                        (f) YOU MUST CONTACT TANK UTILITY IN WRITING WITHIN ONE
                        (1) YEAR OF THE DATE OF THE OCCURRENCE OF THE EVENT OR
                        FACTS GIVING RISE TO A DISPUTE, OR YOU WAIVE THE RIGHT
                        TO PURSUE ANY CLAIM BASED UPON SUCH EVENT, FACTS OR
                        DISPUTE. This arbitration clause shall survive
                        termination of these Terms.
                    </ListItem>
                </List>

                <List>
                    <ListItem sx={{ fontWeight: 'bold' }}>
                        12. Digital Millennium Copyright Act
                    </ListItem>
                </List>
                <List>
                    <ListItem>
                        (a) If you are a copyright owner or an agent thereof and
                        believe that any Content infringes your copyrights, you
                        may submit a notification pursuant to the Digital
                        Millennium Copyright Act (“DMCA”) by providing our
                        Copyright Agent with the following information in
                        writing (see 17 U.S.C 512(c)(3) for further details):
                        (i) A physical or electronic signature of a person
                        authorized to act on behalf of the owner of an exclusive
                        right that is allegedly infringed; (ii) Identification
                        of the copyrighted work claimed to have been infringed
                        or, if multiple copyrighted works at a single online
                        site are covered by a single notification, a
                        representative list of such works at that site; (iii)
                        Identification of the material that is claimed to be
                        infringing or to be the subject of infringing activity
                        and that is to be removed, or access to which is to be
                        disabled, and information reasonably sufficient to
                        permit the service provider to locate the material; (iv)
                        Information reasonably sufficient to permit the service
                        provider to contact you, such as an address, telephone
                        number and, if available, an electronic mail; (v) A
                        statement that you have a good-faith belief that use of
                        the material in the manner complained of is not
                        authorized by the copyright owner, its agent or the law;
                        and (vi) A statement that the information in the
                        notification is accurate and, under penalty of perjury,
                        that you are authorized to act on behalf of the owner of
                        an exclusive right that is allegedly infringed.
                    </ListItem>
                    <ListItem>
                        (b) Tank Utility’s designated Copyright Agent to receive
                        notifications of claimed infringement is Nicholas
                        Mashburn. For clarity, only DMCA notices should go to
                        the Copyright Agent; any other feedback, comments,
                        requests for technical support, and other communications
                        should be directed to Tank Utility customer service
                        through our website. You acknowledge that if you fail to
                        comply with all the requirements of this Section 12(b),
                        your DMCA notice may not be valid.
                    </ListItem>
                    <ListItem>
                        (c) Counter-Notice. If you believe that the Content that
                        was removed (or to which access was disabled) is not
                        infringing, or that you have the authorization from the
                        copyright owner, the copyright owner’s agent or pursuant
                        to the law, to post and use the material in your
                        Content, you may send a counter-notice containing the
                        following information to the Copyright Agent: (i) Your
                        physical or electronic signature; (ii) Identification of
                        the Content that has been removed or to which access has
                        been disabled, and the location at which the Content
                        appeared before it was removed or disabled; (iii) A
                        statement that you have a good-faith belief that the
                        Content was removed or disabled as a result of mistake
                        or misidentification of the Content; and (iv) Your name,
                        address, telephone number and email address, a statement
                        that you consent to the jurisdiction of the federal
                        court in Boston, Massachusetts, USA, and a statement
                        that you will accept service of process from the person
                        who provided notification of the alleged infringement.
                    </ListItem>
                    <ListItem>
                        (d) If a counter-notice is received by the Copyright
                        Agent, Tank Utility may send a copy of the
                        counter-notice to the original complainant informing
                        that person that they may replace the removed Content or
                        cease disabling it in 10 working days. Unless the
                        copyright owner files an action seeking a court order
                        against the Content provider, member or user, the
                        removed Content may be replaced, or access to it
                        restored, in 10 to 14 working days or more after receipt
                        of the counter-notice, at Tank Utility’s sole
                        discretion.
                    </ListItem>
                </List>

                <List>
                    <ListItem sx={{ fontWeight: 'bold' }}>13. General</ListItem>
                </List>
                <List>
                    <ListItem>
                        (a) Changes to these Terms. Tank Utility reserves the
                        right to make changes to these Terms. We will post
                        notice of modifications to these Terms on the Sites. You
                        should ensure that you have read and agree with our most
                        recent Terms when you use the Services. Continued use of
                        the Services following notice or posting of such changes
                        shall indicate your acknowledgment of such changes and
                        agreement to be bound by the revised Terms.
                    </ListItem>
                    <ListItem>
                        (b) Governing Law. The courts in some countries will not
                        apply Massachusetts law to some types of dispute. If you
                        reside in one of those countries, then where
                        Massachusetts law is excluded from applying, your
                        country’s laws will apply to such disputes related to
                        these terms. Otherwise, you agree that these Terms, and
                        any claim, dispute, action, cause of action, issue or
                        request for relief arising out of or relating to these
                        Terms or your use of the Products and Services, shall be
                        governed by the laws of the Commonwealth of
                        Massachusetts without giving effect to any conflict of
                        laws principles that may provide the application of the
                        law of another jurisdiction. You agree to submit to the
                        personal jurisdiction of the state and federal courts
                        in, or for Suffolk County, Massachusetts, USA, for the
                        purpose of litigating all such claims or disputes,
                        unless such claim or dispute is required to be
                        arbitrated as set forth in a section above.
                    </ListItem>
                    <ListItem>
                        (c) Protection of Confidentiality and Intellectual
                        Property Rights. Notwithstanding the foregoing, Tank
                        Utility may seek injunctive or other equitable relief to
                        protect its confidential information and intellectual
                        property rights, or to prevent loss of data or damage to
                        its servers in any court of competent jurisdiction.
                    </ListItem>
                    <ListItem>
                        (d) Entire Agreement/Severability. These Terms
                        constitute the entire agreement between you and Tank
                        Utility regarding the use of the Services. Any failure
                        by Tank Utility to exercise or enforce any right or
                        provision of these Terms shall not operate as a waiver
                        of such right or provision. The section titles in these
                        Terms are for convenience only and have no legal or
                        contractual effect. If any provision of these Terms is,
                        for any reason, held to be invalid or unenforceable, the
                        other provisions of these Terms will be unimpaired, and
                        the invalid or unenforceable provision will be deemed
                        modified so that it is valid and enforceable to the
                        maximum extent permitted by law. Neither party is an
                        agent or partner of the other party.
                    </ListItem>
                    <ListItem>
                        (e) Assignment. These Terms, and any associated rights
                        or obligations, may not be assigned or otherwise
                        transferred by you without Tank Utility’s prior written
                        consent. These Terms may be assigned by Tank Utility
                        without restriction. These Terms are binding upon any
                        permitted assignee.
                    </ListItem>
                    <ListItem>
                        (f) Notifications. Tank Utility may provide
                        notifications to you as required by law, or for
                        marketing or other purposes, via (at its option) email
                        to the primary email associated with your Account, hard
                        copy or posting of such notice on www.tankutility.com.
                        Tank Utility is not responsible for any automatic
                        filtering that you or your network provider may apply to
                        email notifications. Tank Utility recommends adding
                        @Tank Utility.com email addresses to your email address
                        book to help ensure that you receive email notifications
                        from Tank Utility.
                    </ListItem>
                    <ListItem>
                        (g) Contacting Tank Utility. You may contact Tank
                        Utility by going to the Contact Us page of our website
                        www.tankutility.com.
                    </ListItem>
                    <ListItem>
                        (h) Copyright/Trademark Information. Copyright © 2018,
                        Tank Utility. All rights reserved. All trademarks, logos
                        and service marks (“Marks”) displayed on the Services
                        are the property of Tank Utility or of their respective
                        holders. You are not permitted to use any of the Marks
                        without the applicable prior written consent of Tank
                        Utility or such respective holders. Tank Utility
                        reserves the right to alter product and services
                        offerings, specifications and pricing at any time,
                        without notice, and is not responsible for typographical
                        or graphical errors that may appear in these Terms or in
                        related documents.
                    </ListItem>
                </List>
            </DialogContent>
        </FormModal>
    );
};
